/** The Master screen — the frontend half of the archetype.
 *
 * The backend counterpart is archetypes/master.py. Together they mean a
 * master screen is a declaration:
 *
 *     const ITEMS: MasterSpec = {
 *       module: 'masters', object: 'item', title: 'Items',
 *       path: 'masters/items', idKey: 'item_id',
 *       columns: [
 *         { key: 'item_code', label: 'Code' },
 *         { key: 'item_name', label: 'Name' },
 *         { key: 'purchase_rate', label: 'Purchase Rate',
 *           numeric: true, value: true },
 *       ],
 *     }
 *
 * That is a complete screen — search, filter, total, empty state, error
 * state, masking — for roughly 50 of GHERP's 449 screens.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { MasterForm } from './MasterForm'
import { saveFile, type ExportedFile } from '@/lib/download'
import type { Column, ListResult, MasterSpec } from '@/types'
import { singularOf } from '@/lib/label'
import { MasterDetail } from './MasterDetail'
import { useObjectRights } from '@/lib/rights'

type Row = Record<string, unknown>

/** DD-MM-YYYY, app-wide. Parsed from the ISO parts rather than through Date,
 *  so a date-only value never shifts a day across timezones. UI_TASTE.md. */
export function fmtDMY(value: unknown): string {
  if (!value) return '—'
  const s = String(value)
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : s
}

function fmtNumber(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  return Number.isFinite(n)
    ? n.toLocaleString('en-IN', { minimumFractionDigits: 2,
                                  maximumFractionDigits: 2 })
    : String(value)
}

function Cell({ column, row, valuesVisible }:
              { column: Column; row: Row; valuesVisible: boolean }) {
  // A value column the caller may not see was never sent. Rendering the
  // placeholder is describing an absence, not hiding a number that arrived.
  if (column.value && !valuesVisible) {
    return <td className="num masked" title="You do not have permission to see this value">••••</td>
  }
  const raw = row[column.key]
  if (column.numeric) return <td className="num">{fmtNumber(raw)}</td>
  if (column.key.endsWith('_date') || column.key.endsWith('_at')) {
    return <td>{fmtDMY(raw)}</td>
  }
  return <td>{raw === null || raw === undefined || raw === '' ? '—' : String(raw)}</td>
}

export function MasterScreen({ spec }: { spec: MasterSpec }) {
  const [data, setData] = useState<ListResult<Row> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'' | 'Active' | 'Inactive'>('Active')
  const [editing, setEditing] = useState<Row | null | undefined>(undefined)
  // Looking at a record and changing it are different intentions, so they are
  // different states. Double-click views; Edit edits.
  const [viewing, setViewing] = useState<Row | undefined>(undefined)
  const [exporting, setExporting] = useState(false)
  const lastGood = useRef<ListResult<Row> | null>(null)

  // What this person may do here — for deciding what to SHOW. The server
  // checks every request regardless. RIGHTS_MODEL.md 6.1, UI_TASTE.md.
  const rights = useObjectRights(spec.module, spec.object)
  const canAdd = !!spec.fields && rights.can('add')
  const canModify = !!spec.fields && rights.can('modify')   // edit, retire
  const canExport = rights.can('print')

  // WHAT THE SERVER IS ASKED. The search goes with the request: the list is
  // a PAGE, and filtering only what arrived searches 200 of 2,676 items and
  // reports "nothing matches" about a part that plainly exists (12 Sep 2026).
  // Typing is debounced so a search is one request, not one per keystroke.
  const [term, setTerm] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 250)
    return () => clearTimeout(t)
  }, [search])

  const load = useCallback(async () => {
    setError(null)
    try {
      const result = await api.get<ListResult<Row>>(
        `/${spec.path}`, { status: status || undefined,
                           search: term || undefined })
      lastGood.current = result
      setData(result)
    } catch (e) {
      // Never leave the screen on "Loading…", and keep the last good data
      // rather than blanking it. BUILD_GUIDE.md's error-handling standard.
      setError(e instanceof ApiError ? e.message
                                     : 'Please refresh and try again.')
      if (lastGood.current) setData(lastGood.current)
    } finally {
      setLoading(false)
    }
  }, [spec.path, status, term])

  // Re-fetch on open and whenever the tab regains focus, so another user's
  // change appears without a hard refresh. UI_TASTE.md — no constant polling.
  useEffect(() => {
    void load()
    const onFocus = () => {
      const el = document.activeElement
      const typing = el instanceof HTMLElement &&
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
      if (!typing) void load()      // never interrupt someone mid-entry
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [load])

  async function exportList() {
    setExporting(true); setError(null)
    try {
      // The server decides what goes in the file — the same permission and
      // the same masking the screen used. The browser only saves it.
      const file = await api.get<ExportedFile>(
        `/${spec.path}/export`,
        { status: status || undefined, search: term || undefined })
      saveFile(file)
    } catch (e) {
      setError(e instanceof ApiError ? e.message
                                     : 'The export could not be produced.')
    } finally { setExporting(false) }
  }

  async function retire(row: Row) {
    const next = row.status === 'Inactive' ? 'Active' : 'Inactive'
    try {
      await api.patch(`/${spec.path}/${String(row[spec.idKey])}`,
                      { status: next, version: row.version })
      void load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message
                                     : 'The change could not be saved.')
    }
  }

  // Search filters in real time, in memory, over what is already loaded.
  // While the debounce is still running the server is answering the PREVIOUS
  // term, so the rows are narrowed here for instant feedback. Once the server
  // has caught up its answer stands - it searched every row, not this page.
  const rows = useMemo(() => {
    const all = data?.rows ?? []
    const q = search.trim().toLowerCase()
    if (!q || term === search.trim()) return all
    return all.filter(r =>
      spec.columns.some(c => String(r[c.key] ?? '').toLowerCase().includes(q)))
  }, [data, search, term, spec.columns])

  // What the footer says. `total` is how many MATCH; `rows` is what fits on
  // the page.
  const total = data?.total ?? rows.length
  const more = (data?.truncated ?? false) && term === search.trim()

  const visibleColumns = spec.columns
  const valuesVisible = data?.values_visible ?? false

  return (
    <>
      <h1 className="page-title">{spec.title}</h1>
      <p className="page-subtitle">
        {loading && !data ? 'Loading…'
          : `${rows.length.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}`}
      </p>

      {/* The form opens ABOVE the list, never as a separate page.
          `editing === null` is create; a row is edit. UI_TASTE.md. */}
      {viewing !== undefined && editing === undefined && (
        <MasterDetail
          spec={spec}
          record={viewing}
          valuesVisible={valuesVisible}
          canEdit={canModify}
          onEdit={() => { setEditing(viewing); setViewing(undefined) }}
          onClose={() => setViewing(undefined)}
        />
      )}

      {editing !== undefined && spec.fields && (
        <MasterForm
          spec={spec}
          record={editing ?? undefined}
          valuesVisible={valuesVisible}
          onCancel={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined)
            // Re-fetch rather than trusting the local copy, so the screen
            // shows what was actually persisted. UI_TASTE.md.
            void load()
          }}
        />
      )}

      <div className="card">
        <div className="toolbar">
          <input
            type="search"
            placeholder={spec.searchPlaceholder ?? `Search ${spec.title.toLowerCase()}…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ minWidth: 260 }}
            aria-label={`Search ${spec.title}`}
          />
          {spec.hasStatus !== false && (
            <select value={status}
                    onChange={e => setStatus(e.target.value as typeof status)}
                    aria-label="Status">
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="">All</option>
            </select>
          )}
          <div className="grow" />
          {/* Every list exports. ERP_SPECIFICATIONS.md §7 — this is what
              stands in for 144 bespoke report screens. */}
          {canExport && (
            <button className="btn btn-secondary" onClick={() => void exportList()}
                    disabled={exporting || !data?.rows.length}>
              {exporting ? 'Preparing…' : 'Export to Excel'}
            </button>
          )}
          {canAdd && (
            <button className="btn btn-primary"
                    onClick={() => { setEditing(null); setViewing(undefined) }}>
              New {singularOf(spec)}
            </button>
          )}
        </div>

        {error && (
          <div className="error-state">
            Failed to load: {error}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                {visibleColumns.map(c => (
                  <th key={c.key} className={c.numeric ? 'num' : undefined}
                      style={c.width ? { width: c.width } : undefined}>
                    {c.label}
                  </th>
                ))}
                {spec.hasStatus !== false && <th>Status</th>}
                {canModify && <th style={{ width: '150px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={visibleColumns.length + (spec.hasStatus !== false ? 1 : 0) + (canModify ? 1 : 0)} className="loading">
                  Loading…
                </td></tr>
              )}

              {!loading && rows.length === 0 && (
                <tr><td colSpan={visibleColumns.length + (spec.hasStatus !== false ? 1 : 0) + (canModify ? 1 : 0)} className="empty">
                  {search ? `Nothing matches “${search}”.`
                          : `No ${spec.title.toLowerCase()} yet.`}
                </td></tr>
              )}

              {rows.map(row => (
                /* Double-click opens the record. A list shows the few columns
                   that fit; the record holds everything, and reaching it
                   should not mean hunting for a small link at the end of a
                   wide row. The Edit link stays — this is the shortcut, not
                   the only way in. */
                <tr
                  key={String(row[spec.idKey])}
                  className="row-openable"
                  title="Double-click to view"
                  onDoubleClick={() => { setViewing(row); setEditing(undefined) }}
                >
                  {visibleColumns.map(c => (
                    <Cell key={c.key} column={c} row={row}
                          valuesVisible={valuesVisible} />
                  ))}
                  {spec.hasStatus !== false && (
                    <td>
                      <span className={`badge badge-${String(row.status ?? '').toLowerCase()}`}>
                        {String(row.status ?? '—')}
                      </span>
                    </td>
                  )}
                  {canModify && (
                    <td>
                      <div className="row-actions">
                        <button className="link-btn"
                                onClick={() => { setEditing(row); setViewing(undefined) }}>Edit</button>
                        {/* Retire, never delete. The application role holds
                            no DELETE privilege — PROJECT_STATE.md #18. */}
                        <button
                          className="link-btn"
                          onClick={() => void retire(row)}
                        >
                          {row.status === 'Inactive' ? 'Reinstate' : 'Mark Inactive'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}

              {/* Inside the table body, so it recomputes on every keystroke
                  and always reflects exactly the rows visible. UI_TASTE.md. */}
              {rows.length > 0 && (
                <tr className="total-row">
                  <td colSpan={visibleColumns.length + (spec.hasStatus !== false ? 1 : 0) + (canModify ? 1 : 0)} className="num">
                    {/* One message, and inside the table body so it follows
                        the filter. UI_TASTE.md. */}
                    {more
                      ? <>Showing {rows.length.toLocaleString('en-IN')} of{' '}
                          {total.toLocaleString('en-IN')} {spec.title.toLowerCase()}
                          {' '}— <span className="row-muted">search to narrow it</span></>
                      : <>Total: {total.toLocaleString('en-IN')} {spec.title.toLowerCase()}</>}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
